/**
 * The palettes a visitor may draw the application in, and what a stored theme
 * resolves to (#893).
 *
 * A palette is token values in the brandbook's stylesheet, keyed by
 * `data-palette` beside the `light`/`dark` class it belongs to, so the mode is
 * part of the palette rather than a second choice beside it. Light and Dark are
 * the stylesheet's own two, and carry no attribute.
 *
 * The order is the order the settings offer them in.
 */
export const PALETTES = [
  {
    id: "light",
    mode: "light",
    swatch: ["#e8e1d9", "#eee9e2", "#84430b", "#4f40bf"],
  },
  {
    id: "dark",
    mode: "dark",
    swatch: ["#15111d", "#241d30", "#ffa13d", "#8a7aff"],
  },
  {
    id: "embers",
    mode: "dark",
    swatch: ["#141110", "#1f1a17", "#d48a4e", "#c2a982"],
  },
  {
    id: "forest",
    mode: "dark",
    swatch: ["#0d1311", "#16201b", "#c9a35f", "#7fb39a"],
  },
  {
    id: "midnight",
    mode: "dark",
    swatch: ["#0c1016", "#151c26", "#e8a26a", "#86a6d1"],
  },
  {
    id: "graphite",
    mode: "dark",
    swatch: ["#111111", "#1b1b1b", "#f07a3a", "#c4c4c4"],
  },
  {
    id: "parchment",
    mode: "light",
    swatch: ["#ebe5da", "#f6f2ea", "#9a4f1f", "#3f5c4d"],
  },
  {
    id: "neon",
    mode: "dark",
    swatch: ["#08080f", "#11111d", "#ff2e88", "#00e5ff"],
  },
  {
    id: "toxic",
    mode: "dark",
    swatch: ["#070a07", "#0f160f", "#a6ff00", "#00ffa3"],
  },
  {
    id: "crimson",
    mode: "dark",
    swatch: ["#0b0606", "#170d0d", "#d42a33", "#d4a24c"],
  },
  {
    id: "arctic",
    mode: "dark",
    swatch: ["#060a0f", "#0d1520", "#3dd6ff", "#a78bff"],
  },
] as const;

export type Palette = (typeof PALETTES)[number];
export type PaletteId = Palette["id"];
export type Mode = Palette["mode"];
/** What is stored: a palette, or the operating system's choice of Light or Dark. */
export type Theme = PaletteId | "system";

/** Every theme the visitor may choose, in the order they are offered. */
export const THEME_CHOICES: readonly Theme[] = [
  "system",
  ...PALETTES.map(({ id }) => id),
];

/**
 * What a visitor who never chose, or whose stored value names nothing, is
 * drawn in (#896): System, so the application starts in whatever the visitor's
 * device already prefers instead of picking a palette for them.
 */
export const DEFAULT_THEME: Theme = "system";

const MODES: Record<string, Mode> = Object.fromEntries(
  PALETTES.map(({ id, mode }) => [id, mode]),
);

export const isTheme = (value: unknown): value is Theme =>
  value === "system" ||
  (typeof value === "string" && Object.hasOwn(MODES, value));

export interface ResolvedPalette {
  palette: PaletteId;
  mode: Mode;
}

export function resolvePalette(
  theme: Theme,
  prefersDark: boolean,
): ResolvedPalette {
  if (theme === "system") {
    return prefersDark
      ? { palette: "dark", mode: "dark" }
      : { palette: "light", mode: "light" };
  }
  return { palette: theme, mode: MODES[theme] };
}

/** The `data-palette` value the stylesheet keys on; none for its own two. */
export const paletteAttribute = (palette: PaletteId): string | null =>
  palette === "light" || palette === "dark" ? null : palette;

/**
 * The script that applies the stored palette before first paint, built from
 * the list above so the two cannot disagree. It runs before the bundle, so it
 * is a string; anything it cannot read falls back to the default theme. The
 * unknown-value check runs before System resolves, because the default is
 * System itself and `MODES` maps palettes only.
 */
export const paletteBootstrapScript = (storageKey: string): string =>
  `(function(){var m=${JSON.stringify(MODES)},r=document.documentElement,t;` +
  `try{t=localStorage.getItem(${JSON.stringify(storageKey)})}catch(e){}` +
  `if(t!=="system"&&!Object.prototype.hasOwnProperty.call(m,t))t=${JSON.stringify(DEFAULT_THEME)};` +
  `if(t==="system")t=window.matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light";` +
  `r.classList.add(m[t]);if(t!=="light"&&t!=="dark")r.setAttribute("data-palette",t);})();`;
